import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/i18n";

/* Tick = own. Optimistic: the state flips before the network answers, and flips back
   if it fails. Anonymous: the box is visible, a click sends to sign-up and back here.
   Quantity stays hidden (×1 is the norm): "×N" only appears above 1, tap it to edit. */
export function OwnToggle({ cardId, qty, onChange, locked, testid }) {
  const { me } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const owned = qty > 0;

  const save = async (next) => {
    const prev = qty;
    onChange(next);
    try {
      if (next > 0) await api.put(`/collection/cards/${cardId}`, { quantity: next });
      else await api.delete(`/collection/cards/${cardId}`);
    } catch (e) {
      onChange(prev);
      if (e?.response?.status === 402) toast(t("sets.coll_locked"), { action: { label: t("sets.coll_locked_short"), onClick: () => navigate("/pricing") } });
      else toast.error(t("sets.coll_error"));
    }
  };
  const toggle = () => {
    if (!me) {
      toast(t("sets.coll_sign_in"));
      navigate(`/auth?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (locked && !owned) { toast(t("sets.coll_locked"), { action: { label: t("sets.coll_locked_short"), onClick: () => navigate("/pricing") } }); return; }
    save(owned ? 0 : 1);
  };

  return (
    <span className="inline-flex items-center gap-1">
      <button type="button" role="checkbox" aria-checked={owned} aria-label={t("sets.coll_own_label")} onClick={toggle}
        className="wl-own-box" data-owned={owned} data-testid={testid || `own-toggle-${cardId}`}>
        {owned && <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
      </button>
      {owned && !editing && (
        <button type="button" onClick={() => setEditing(true)} className={`wl-mono min-h-[28px] px-1 text-[10px] tabular-nums ${qty > 1 ? "text-[var(--wl-accent)]" : "text-[var(--wl-muted)]/60"}`} aria-label={t("sets.coll_qty")} data-testid={`own-qty-${cardId}`}>
          ×{qty}
        </button>
      )}
      {owned && editing && (
        <span className="inline-flex items-center border border-[var(--wl-line)] bg-[var(--wl-panel)]" data-testid={`own-qty-editor-${cardId}`}>
          <button type="button" onClick={() => save(Math.max(1, qty - 1))} className="wl-mono min-h-[32px] min-w-[32px] text-[12px] text-[var(--wl-text)] hover:text-[var(--wl-accent)]" data-testid={`own-qty-minus-${cardId}`}>−</button>
          <span className="wl-mono px-1 text-[11px] font-bold tabular-nums text-[var(--wl-accent)]">{qty}</span>
          <button type="button" onClick={() => save(Math.min(999, qty + 1))} className="wl-mono min-h-[32px] min-w-[32px] text-[12px] text-[var(--wl-text)] hover:text-[var(--wl-accent)]" data-testid={`own-qty-plus-${cardId}`}>+</button>
          <button type="button" onClick={() => setEditing(false)} className="wl-mono min-h-[32px] px-2 text-[9px] uppercase text-[var(--wl-muted)] hover:text-[var(--wl-text)]" data-testid={`own-qty-done-${cardId}`}>OK</button>
        </span>
      )}
    </span>
  );
}

export function ProgressBar({ owned, total, pct, testid, compact }) {
  return (
    <div className={compact ? "h-1 w-full bg-[var(--wl-line)]" : "h-1.5 w-full bg-[var(--wl-line)]"} data-testid={testid} aria-valuenow={owned} aria-valuemax={total} role="progressbar">
      <div className="h-full bg-[var(--wl-accent)] transition-[width] duration-300" style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}
